import Link from "next/link";
import { graphql, useFragment } from "react-relay";
import { Tag, Text } from "@olivahealth/oli-ui";
import { useTranslation } from "react-i18next";
import logger from "@olivahealth/logger/client";
import Image from "../../../components/common/Image";
import MarkAsFavouriteButton from "../MarkAsFavouriteButton";
import { useUserData } from "../../../services/contexts/UserDataContext";
import * as s from "./styles";
import { ResourceCard_resource$key } from "./__generated__/ResourceCard_resource.graphql";

const ResourceCardFragment = graphql`
  fragment ResourceCard_resource on Resource {
    imageCard
    id
    type
    name
    slug
    format
    isFavourite
  }
`;

interface Props {
  resource: ResourceCard_resource$key;
  isOrganisationResource?: boolean;
  variant?: "imageFull" | "panel";
  showMarkAsFavouriteButton?: boolean;
  darkMode?: boolean;
  onFavouriteToggle?: () => void;
}

export default function ResourceCard({
  resource,
  variant = "imageFull",
  isOrganisationResource = false,
  showMarkAsFavouriteButton = false,
  darkMode = false,
  onFavouriteToggle,
}: Props) {
  const { t } = useTranslation("common", { keyPrefix: "resourceCard" });
  const { data: user } = useUserData();
  const data = useFragment<ResourceCard_resource$key>(
    ResourceCardFragment,
    resource,
  );

  if (!data) {
    logger.warn(
      "ResourceCard",
      "data is undefined for a given resource, not rendering card",
      {
        userId: user?.id ?? "",
      },
    );
    return null;
  }

  const PanelVariant = () => {
    return (
      <div className={darkMode ? s.wrapperDark : s.wrapper}>
        <div className={s.imageContainer}>
          <Image
            src={data?.imageCard || "/img/img_background_self_guided_area.png"}
            fill
            sizes="(max-width: 768px) 100vw, 30vw"
            className="object-cover"
            alt={t("imageAlt", { title: name })}
          />
        </div>
        <div className={s.metadataContainer}>
          <Text weight="bold" color={darkMode ? "white" : undefined}>
            {data?.name}
          </Text>
          <span className="flex items-center justify-between">
            <span className="flex gap-x-3 items-center">
              <Tag theme={darkMode ? "transparent" : "medium"}>
                <Text color={darkMode ? "white" : undefined}>
                  {data?.type.charAt(0).toUpperCase() +
                    data?.type.slice(1).toLowerCase()}
                </Text>
              </Tag>
              <Text color={darkMode ? "white" : "secondary"} weight="medium">
                {data?.format}
              </Text>
            </span>
            {showMarkAsFavouriteButton && (
              <MarkAsFavouriteButton
                resourceId={data.id}
                isFavourite={data.isFavourite}
                variant="icon"
                onFavouriteToggle={onFavouriteToggle}
              />
            )}
          </span>
        </div>
      </div>
    );
  };

  const FullImageVariant = () => {
    return (
      <div className={s.wrapperFullImage}>
        <div className={s.imageOverlay} />
        <Image
          src={data?.imageCard || "/img/img_background_self_guided_area.png"}
          sizes="(max-width: 768px) 100vw, 30vw"
          fill
          className="rounded-2xl object-cover"
          alt={t("imageAlt", { title: name })}
        />
        <div
          className={`${s.fullImageContentWrapper} flex flex-col justify-end`}
        >
          <span className="flex flex-col gap-y-2">
            <div className={s.nameContainer}>
              <Text weight="bold" color="white">
                {data?.name}
              </Text>
            </div>
            <span className="flex justify-between items-end">
              <div className="flex gap-x-1 h-fit items-center">
                <Tag size="xs" theme="transparent" weight="bold">
                  {data?.type.charAt(0).toUpperCase() +
                    data?.type.slice(1).toLowerCase()}
                </Tag>
                <Tag size="xs" theme="transparent" weight="bold">
                  {data?.format}
                </Tag>
              </div>
              {showMarkAsFavouriteButton && (
                <div className="w-fit">
                  <MarkAsFavouriteButton
                    resourceId={data?.id}
                    isFavourite={data?.isFavourite}
                    variant="icon"
                    onFavouriteToggle={onFavouriteToggle}
                  />
                </div>
              )}
            </span>
          </span>
        </div>
      </div>
    );
  };

  return (
    <Link
      href={`${
        isOrganisationResource ? "/organisation/resources" : "/practices"
      }/${data?.slug}`}
      className="h-full"
      data-testid={`resource-card-${name}`}
    >
      {variant === "panel" ? <PanelVariant /> : <FullImageVariant />}
    </Link>
  );
}
